import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getSite, deleteSite } from '../../../models/sitesModel';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import Button from '../../../components/common/Button';

function SiteView() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchSiteData() {
      try {
        setLoading(true);
        const response = await getSite(siteId);
        
        if (response.status === 1 && response.row) {
          setSite(response.row);
        } else {
          toast.error('Site not found');
          navigate('/dashboard/sites');
        }
      } catch (error) {
        console.error('Error fetching site:', error);
        toast.error('Error loading site data');
      } finally {
        setLoading(false);
      }
    }

    fetchSiteData();
  }, [siteId, navigate]);

  const handleDelete = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await deleteSite(siteId);
          
          if (response.status === 1) {
            toast.success('Site deleted successfully');
            navigate('/dashboard/sites');
          } else {
            toast.error(response.message || 'Failed to delete site');
          }
        } catch (error) {
          toast.error(error.response?.data?.message || 'Error deleting site');
          console.error('Error deleting site:', error);
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="p-4 py-6 md:p-8 flex justify-center items-center h-[calc(100vh-150px)]">
        <div 
          className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4" 
          style={{ borderColor: 'var(--color-secondary-default)' }}
        ></div>
      </div>
    );
  }

  if (!site) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-semibold text-gray-800">Site not found</h2>
        <p className="mt-2 text-gray-600">The site you are looking for does not exist or has been removed.</p>
        <Button 
          className="mt-4"
          onClick={() => navigate('/dashboard/sites')}
        >
          Back to Sites
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">{site.site_name}</h1>
        <div className="space-x-2">
          {can('Update Site') && (
            <Button 
              onClick={() => navigate(`/dashboard/sites/edit/${siteId}`)}
              variant="outline"
            >
              Edit
            </Button>
          )}
          
          {can('Delete Site') && (
            <Button 
              onClick={handleDelete}
              variant="danger"
            >
              Delete
            </Button>
          )}
          
          <Button
            onClick={() => navigate('/dashboard/sites')}
            variant="light"
          >
            Back to Sites
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <div className="bg-white shadow-sm rounded-lg p-6 border border-gray-200">
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-2">Site Details</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium">{site.site_name}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Type</p>
                    <p className="font-medium">{site.site_type || 'Not specified'}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <div className="mt-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        site.site_status === 'Active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {site.site_status || 'Unknown'}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Address</p>
                    <p className="font-medium">{site.site_address || 'Not provided'}</p>
                  </div>
                </div>
              </div>
              
              {(site.latitude || site.longitude) && (
                <div>
                  <h2 className="text-lg font-semibold mb-2">Location</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Latitude</p>
                      <p className="font-medium">{site.latitude}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-500">Longitude</p>
                      <p className="font-medium">{site.longitude}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {site.notes && (
                <div>
                  <h2 className="text-lg font-semibold mb-2">Notes</h2>
                  <p className="text-gray-700 whitespace-pre-wrap">{site.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div>
          <div className="bg-white shadow-sm rounded-lg p-6 border border-gray-200">
            <h2 className="text-lg font-semibold mb-4">Site Information</h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Created</p>
                <p className="font-medium">
                  {site.created_at ? new Date(site.created_at).toLocaleDateString() : 'Unknown'}
                </p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Last Updated</p>
                <p className="font-medium">
                  {site.updated_at ? new Date(site.updated_at).toLocaleDateString() : 'Unknown'}
                </p>
              </div>
              
              {site.created_by && (
                <div>
                  <p className="text-sm text-gray-500">Created By</p>
                  <p className="font-medium">{site.created_by}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SiteView;
